import { db } from '../db';
import { isFeatureEnabled } from '../config/featureFlags';
import { databaseOperations } from './DatabaseQueue';
import { getMediaType, getMediaTypeFromUrl } from '../utils/MediaOperations';
import { setReceived } from '../api-helper';
import electronBridge from '../utils/electronBridge';

const isUrl = (str) => {
    if (!str) return false;
    const trimmed = str.trim().toLowerCase();
    return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
};

const validateMediaUrl = async (url) => {
    if (!url || !isUrl(url)) return false;
    try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
    } catch {
        return false;
    }
};

const getBase64Prefix = (mediaType) => {
    switch (mediaType?.toLowerCase()) {
        case 'video': return 'data:video/mp4;base64,';
        case 'audio': return 'data:audio/mpeg;base64,';
        case '3d': return 'data:model/gltf-binary;base64,';
        default: return 'data:image/webp;base64,';
    }
};

export async function saveGeneratedMedia({
    imageData,
    base64Data = null,
    jobUid,
    outputUid,
    seed = null,
    model = '',
    prompt = '',
    tag = '',
    isOnline = true,
    serverUrl = null,
    dataType = null,
    localDirectory = null,
    notifyApi = true,
}) {
    const hash = outputUid;

    console.log('[MediaStorageService] Saving media:', { jobUid, outputUid, hash });

    try {
        const existingRecord = await db.images.where('hash').equals(hash).first();
        if (existingRecord && existingRecord.image && existingRecord.image !== '') {
            console.log(`[MediaStorageService] Media with hash ${hash} already exists, skipping`);
            return {
                id: existingRecord.id,
                hash,
                success: true,
                alreadyExists: true
            };
        }

        let finalImageData = imageData;
        let detectedDataType = dataType;

        if (electronBridge.isElectron && localDirectory && base64Data) {
            try {
                const mediaType = detectedDataType || await getMediaType(base64Data);
                const savedFilePath = await electronBridge.ipcRenderer.invoke(
                    "test_upload",
                    base64Data,
                    localDirectory,
                    JSON.stringify([]),
                    mediaType
                );
                if (savedFilePath) {
                    finalImageData = savedFilePath;
                    console.log(`[MediaStorageService] Saved to local file: ${savedFilePath}`);
                }
            } catch (err) {
                console.warn('[MediaStorageService] Failed to save to local file, using URL/base64:', err);
            }
        }

        if (!electronBridge.isElectron && imageData && isUrl(imageData)) {
            const isValidUrl = await validateMediaUrl(imageData);
            if (!isValidUrl && base64Data) {
                console.log('[MediaStorageService] URL validation failed, using base64 fallback');
                finalImageData = base64Data;
            }
        }

        if (!detectedDataType) {
            if (serverUrl || (imageData && isUrl(imageData))) {
                detectedDataType = getMediaTypeFromUrl(serverUrl || imageData);
            } else if (base64Data || (finalImageData && !isUrl(finalImageData))) {
                const rawBase64 = base64Data || finalImageData;
                if (rawBase64 && !rawBase64.startsWith('data:')) {
                    detectedDataType = await getMediaType(rawBase64);
                }
            }
            detectedDataType = detectedDataType || 'image';
        }

        if (finalImageData && !isUrl(finalImageData) && !finalImageData.startsWith('data:')) {
            if (!electronBridge.isElectron) {
                finalImageData = `${getBase64Prefix(detectedDataType)}${finalImageData}`;
            }
        }

        const imageRecord = {
            image: finalImageData,
            seed: seed,
            uid: jobUid,
            model: model,
            inCollection: false,
            hash: hash,
            online: isOnline,
            tag: tag,
            prompt: prompt,
            receivedtimestamp: Date.now(),
            requestedtimestamp: Date.now(),
            dataType: detectedDataType?.toLowerCase() || 'image'
        };

        let recordId;

        if (existingRecord && (!existingRecord.image || existingRecord.image === '')) {
            console.log('[MediaStorageService] Updating existing placeholder');

            if (existingRecord.hash && existingRecord.hash !== hash) {
                const conflictCheck = await db.images.where('hash').equals(hash).first();
                if (conflictCheck && conflictCheck.id !== existingRecord.id) {
                    console.error(`[MediaStorageService] Hash conflict: ${hash} already used by record ${conflictCheck.id}`);
                    return { id: null, hash, success: false, error: 'Hash conflict' };
                }
            }

            if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                await databaseOperations.updateImage(existingRecord.id, imageRecord);
            } else {
                await db.images.update(existingRecord.id, imageRecord);
            }
            recordId = existingRecord.id;
        } else {
            console.log('[MediaStorageService] Adding new image record');

            if (isFeatureEnabled('USE_DATABASE_QUEUE')) {
                recordId = await databaseOperations.addImage(imageRecord);
            } else {
                recordId = await db.images.put(imageRecord);
            }
        }

        console.log('[MediaStorageService] Media saved successfully:', { id: recordId, hash });

        if (isOnline && notifyApi) {
            try {
                await setReceived(jobUid, outputUid);
                console.log('[MediaStorageService] API notified of received media:', { jobUid, outputUid });
            } catch (err) {
                console.warn('[MediaStorageService] Failed to notify API of received media:', err);
            }
        }

        return {
            id: recordId,
            hash,
            success: true,
            alreadyExists: false,
            dataType: detectedDataType
        };

    } catch (error) {
        if (error.name === 'ConstraintError') {
            console.log(`[MediaStorageService] ConstraintError for hash ${hash} - already exists`);
            return { id: null, hash, success: true, alreadyExists: true };
        }

        console.error('[MediaStorageService] Error saving media:', error);
        return {
            id: null,
            hash,
            success: false,
            error: error.message
        };
    }
}

export async function checkMediaExists(hash) {
    try {
        const record = await db.images.where('hash').equals(hash).first();
        return {
            exists: !!record,
            hasImage: record?.image && record.image !== '',
            id: record?.id || null
        };
    } catch (error) {
        console.error('[MediaStorageService] Error checking media exists:', error);
        return { exists: false, hasImage: false, id: null };
    }
}

export async function getMediaByHash(hash) {
    try {
        return await db.images.where('hash').equals(hash).first();
    } catch (error) {
        console.error('[MediaStorageService] Error getting media by hash:', error);
        return null;
    }
}

const MediaStorageService = {
    saveGeneratedMedia,
    checkMediaExists,
    getMediaByHash,
};

export default MediaStorageService;
